import React, { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import { useAuthStore } from '../store';
import { useMyList } from '../lib/my-list-context';
import { TitleCard } from './title-card';
import { Button } from './ui/button';
import { Link } from './common/NextAdapters';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const SORT_OPTIONS = [
  { id: 'latest', label: 'Recently Added' },
  { id: 'alphabetical', label: 'Title (A–Z)' },
  { id: 'year', label: 'Release Year (Newest)' },
  { id: 'likes', label: 'Most Popular' },
];

export function MyListView() {
  const token = useAuthStore((s) => s.token);
  const { list, isLoading, removeFromList } = useMyList();

  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'movies' | 'series'
  const [sortBy, setSortBy] = useState('latest');
  const [searchQuery, setSearchQuery] = useState('');

  const movieCount = useMemo(
    () => list.filter((item) => !item.isSeries).length,
    [list]
  );
  const seriesCount = useMemo(
    () => list.filter((item) => item.isSeries).length,
    [list]
  );

  // Filtered and sorted titles
  const filtered = useMemo(() => {
    let result = [...list];

    // Filter by tab
    if (activeTab === 'movies') {
      result = result.filter((item) => !item.isSeries);
    } else if (activeTab === 'series') {
      result = result.filter((item) => item.isSeries);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.title?.toLowerCase().includes(q) ||
          item.category?.toLowerCase().includes(q) ||
          item.director?.toLowerCase().includes(q)
      );
    }

    // Sort
    switch (sortBy) {
      case 'alphabetical':
        result.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'year':
        result.sort((a, b) => (b.year || 0) - (a.year || 0));
        break;
      case 'likes':
        result.sort((a, b) => (b.likes || 0) - (a.likes || 0));
        break;
      case 'latest':
      default:
        break;
    }

    return result;
  }, [list, activeTab, sortBy, searchQuery]);

  return (
    <div className="min-h-[75vh] pb-24">
      {/* ── Masthead ──────────────────────────────────────────────── */}
      <section className="relative isolate overflow-hidden border-b border-white/8 bg-gradient-to-b from-white/[0.04] via-transparent to-transparent pt-28 pb-10 sm:pt-36 sm:pb-14 -mt-16">
        <div className="mx-auto max-w-[1600px] gutter">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-white uppercase backdrop-blur-md">
                <span className="size-1.5 rounded-full bg-violet animate-pulse" />
                Personal Watchlist
              </div>
              <h1 className="display mt-4 text-[clamp(2.5rem,6vw,4.25rem)] text-balance font-extrabold text-foreground">
                My List
              </h1>
              <p className="mt-2 text-base text-muted-foreground">
                Your curated collection of movies and TV series ready to stream.
              </p>
            </div>

            {list.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-4 py-2 text-sm font-medium text-foreground/80 backdrop-blur-sm">
                  <Icon icon="ph:film-reel-fill" className="size-4 text-violet" />
                  <span>
                    {list.length} {list.length === 1 ? 'title' : 'titles'} saved
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Guest sync notice */}
          {!token && list.length > 0 && (
            <div className="mt-6 flex flex-col items-start justify-between gap-3 rounded-2xl border border-violet/30 bg-violet/10 p-4 backdrop-blur-md sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-violet/20 text-violet">
                  <Icon icon="ph:cloud-arrow-up-bold" className="size-5" />
                </div>
                <p className="text-sm text-white/90">
                  <span className="font-semibold text-white">Guest Watchlist:</span>{' '}
                  Your saved titles are currently stored locally. Sign in to sync across all your devices.
                </p>
              </div>
              <Button asChild size="sm" className="shrink-0 rounded-full bg-white text-black hover:bg-white/90">
                <Link href="/login">Sign in now</Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ── Main Content Area ─────────────────────────────────────── */}
      <div className="mx-auto max-w-[1600px] gutter pt-8">
        {/* Filter & Search Bar */}
        {list.length > 0 && (
          <div className="flex flex-col gap-4 border-b border-white/8 pb-6 md:flex-row md:items-center md:justify-between">
            {/* Type tabs */}
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
              <button
                type="button"
                onClick={() => setActiveTab('all')}
                className={cn(
                  'flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 cursor-pointer',
                  activeTab === 'all'
                    ? 'brand-sweep text-white shadow-md'
                    : 'border border-white/12 text-muted-foreground hover:border-white/25 hover:text-foreground'
                )}
              >
                All
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {list.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('movies')}
                className={cn(
                  'flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 cursor-pointer',
                  activeTab === 'movies'
                    ? 'brand-sweep text-white shadow-md'
                    : 'border border-white/12 text-muted-foreground hover:border-white/25 hover:text-foreground'
                )}
              >
                Movies
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {movieCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('series')}
                className={cn(
                  'flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 cursor-pointer',
                  activeTab === 'series'
                    ? 'brand-sweep text-white shadow-md'
                    : 'border border-white/12 text-muted-foreground hover:border-white/25 hover:text-foreground'
                )}
              >
                TV Series
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">
                  {seriesCount}
                </span>
              </button>
            </div>

            {/* Right: Search & Sort */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Search within list */}
              <div className="relative min-w-[200px] flex-1 sm:w-64 sm:flex-none">
                <Icon
                  icon="ph:magnifying-glass-bold"
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground"
                />
                <input
                  type="text"
                  placeholder="Filter my list..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 w-full rounded-full border border-white/15 bg-white/5 pl-9 pr-8 text-sm text-foreground outline-none transition-all placeholder:text-muted-foreground focus:border-white/30 focus:bg-white/10"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <Icon icon="ph:x-bold" className="size-3.5" />
                  </button>
                )}
              </div>

              {/* Sort dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex h-10 items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 text-sm font-medium text-foreground/90 transition-colors hover:border-white/30 hover:bg-white/10 focus:outline-none"
                  >
                    <Icon icon="ph:funnel-simple-bold" className="size-4 text-muted-foreground" />
                    <span>
                      {SORT_OPTIONS.find((o) => o.id === sortBy)?.label}
                    </span>
                    <Icon icon="ph:caret-down-bold" className="size-3 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 rounded-xl border border-white/12 bg-popover/95 p-1 backdrop-blur-xl">
                  {SORT_OPTIONS.map((opt) => (
                    <DropdownMenuItem
                      key={opt.id}
                      onClick={() => setSortBy(opt.id)}
                      className={cn(
                        'flex items-center justify-between rounded-lg px-3 py-2 text-sm cursor-pointer',
                        sortBy === opt.id
                          ? 'bg-white/10 font-semibold text-white'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {opt.label}
                      {sortBy === opt.id && (
                        <Icon icon="ph:check-bold" className="size-4 text-violet" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}

        {/* Loading state */}
        {isLoading && (
          <div className="grid grid-cols-2 gap-4 pt-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[2/3] w-full rounded-xl bg-white/5 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && list.length === 0 && (
          <div className="flex min-h-[48vh] flex-col items-center justify-center py-16 text-center">
            <div className="relative flex size-24 items-center justify-center rounded-3xl border border-white/15 bg-white/5 shadow-2xl backdrop-blur-md">
              <Icon icon="ph:bookmark-simple" className="size-12 text-muted-foreground/60" />
              <div className="absolute -bottom-1 -right-1 flex size-8 items-center justify-center rounded-full bg-violet text-white shadow-lg">
                <Icon icon="ph:plus-bold" className="size-4" />
              </div>
            </div>

            <h2 className="display-sm mt-6 text-2xl sm:text-3xl font-bold">
              Your list is empty
            </h2>
            <p className="mt-2.5 max-w-md text-sm text-muted-foreground sm:text-base">
              Add movies and TV series to your list while browsing so you can easily track and stream them whenever you want.
            </p>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild size="lg" className="h-11 rounded-full bg-white px-7 font-semibold text-black hover:bg-white/90">
                <Link href="/browse?type=Movie">
                  <Icon icon="ph:film-slate-bold" className="mr-2 size-4" />
                  Explore Movies
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 rounded-full border-white/25 bg-white/5 px-7 font-medium text-white hover:bg-white/10">
                <Link href="/browse?type=Series">
                  <Icon icon="ph:television-bold" className="mr-2 size-4" />
                  Explore Series
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Empty filter results */}
        {!isLoading && list.length > 0 && filtered.length === 0 && (
          <div className="py-20 text-center">
            <Icon icon="ph:magnifying-glass" className="mx-auto size-12 text-muted-foreground/40" />
            <h3 className="display-sm mt-4 text-xl font-bold">No titles matched your search</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Try adjusting your search query or switching to another category.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchQuery('');
                setActiveTab('all');
              }}
              className="mt-5 rounded-full border-white/20"
            >
              Reset filters
            </Button>
          </div>
        )}

        {/* Titles Grid */}
        {!isLoading && filtered.length > 0 && (
          <div className="grid grid-cols-2 gap-4 pt-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {filtered.map((title) => (
              <div key={title.id} className="group/item relative flex flex-col">
                <TitleCard title={title} />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeFromList(title.id);
                  }}
                  title="Remove from My List"
                  aria-label={`Remove ${title.title} from My List`}
                  className="absolute top-2 right-2 z-30 flex size-8 items-center justify-center rounded-full border border-white/20 bg-black/70 text-white/80 opacity-0 backdrop-blur-md transition-all duration-200 hover:border-rose-500/50 hover:bg-rose-500/20 hover:text-rose-300 group-hover/item:opacity-100 cursor-pointer"
                >
                  <Icon icon="ph:x-bold" className="size-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyListView;
